import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { apiFetch, toCurrency, downloadCsv } from '../utils';
import UserExpensesModal from './UserExpensesModal';

const CACHE_STEPS = [
  { key: 'users', label: 'User Details', path: '/cacheUserDetails' },
  { key: 'contacts', label: 'Contact Details', path: '/cacheContactsDetails' },
  { key: 'expenses', label: 'Expenses', path: '/cacheExpenses' },
  { key: 'bankTx', label: 'Bank Transactions', path: '/cacheBankTransactions' },
];

const EXPENSE_PAYMENTS_STEP = { key: 'expensePayments', label: 'Expense Payments', path: '/expensesPayments' };

const ALL_ITEMS = [...CACHE_STEPS, EXPENSE_PAYMENTS_STEP];

function formatFileDate(iso) {
  if (!iso) return 'None';
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
}

export default function Expenses() {
  const [fileDates, setFileDates] = useState({});
  const [stepStatus, setStepStatus] = useState({});
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    loadInitialState();
  }, []);

  async function loadInitialState() {
    try {
      const stats = await apiFetch('/getFileStats');
      if (stats) setFileDates(stats);
    } catch {
      // ignore
    }

    try {
      const data = await apiFetch('/getUserData');
      const list = Array.isArray(data?.users) ? data.users : Array.isArray(data) ? data : [];
      if (list.length > 0) {
        setUsers([...list].sort((a, b) => (a.first_name || '').localeCompare(b.first_name || '')));
      }
    } catch {
      // ignore
    }
  }

  async function handleRefresh() {
    setStepStatus({});
    setUsers([]);

    // Run the four cache steps in parallel first
    const results = await Promise.allSettled(
      CACHE_STEPS.map(async (item) => {
        setStepStatus((prev) => ({ ...prev, [item.key]: 'loading' }));
        try {
          const data = await apiFetch(item.path);
          setStepStatus((prev) => ({ ...prev, [item.key]: 'complete' }));
          return { key: item.key, data };
        } catch (err) {
          setStepStatus((prev) => ({ ...prev, [item.key]: 'error' }));
          throw err;
        }
      })
    );

    const usersResult = results[0];
    if (usersResult.status === 'fulfilled') {
      const data = usersResult.value?.data;
      const list = Array.isArray(data?.users) ? data.users : Array.isArray(data) ? data : [];
      setUsers([...list].sort((a, b) => (a.first_name || '').localeCompare(b.first_name || '')));
    }

    // Expense payments depends on cached files, so run after the others finish
    setStepStatus((prev) => ({ ...prev, [EXPENSE_PAYMENTS_STEP.key]: 'loading' }));
    try {
      await apiFetch(EXPENSE_PAYMENTS_STEP.path);
      setStepStatus((prev) => ({ ...prev, [EXPENSE_PAYMENTS_STEP.key]: 'complete' }));
    } catch {
      setStepStatus((prev) => ({ ...prev, [EXPENSE_PAYMENTS_STEP.key]: 'error' }));
    }

    try {
      const stats = await apiFetch('/getFileStats');
      if (stats) setFileDates(stats);
    } catch {
      // ignore
    }
    setStepStatus({});
  }

  const isRefreshing = Object.values(stepStatus).some((s) => s === 'loading');

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-slate-900">Expenses</h2>

      {/* Data files status */}
      <div className="rounded-xl bg-white p-5 shadow-md">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">Data Loading</h3>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-slate-100 px-3 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <svg className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
        <ul className="divide-y divide-slate-100">
          {ALL_ITEMS.map((item) => (
            <li key={item.key} className="flex items-center justify-between py-3">
              <span className="text-sm text-slate-700">{item.label}</span>
              {stepStatus[item.key] === 'loading' ? (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700">
                  <div className="h-3 w-3 animate-spin rounded-full border-2 border-amber-200 border-t-amber-600" />
                  Loading
                </span>
              ) : stepStatus[item.key] === 'error' ? (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700">
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Error
                </span>
              ) : (
                <span className="text-xs font-medium text-slate-500">
                  {formatFileDate(fileDates[item.key])}
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* Users list */}
      {users.length > 0 && (
        <div className="rounded-xl bg-white shadow-md">
          <div className="flex items-center justify-between px-5 py-4">
            <h3 className="text-sm font-semibold text-slate-900">
              Users ({users.length})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-5 py-3">First Name</th>
                  <th className="px-5 py-3">Last Name</th>
                  <th className="px-5 py-3">URL</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((user) => (
                  <tr
                    key={user.url}
                    onClick={() => setSelectedUser(user)}
                    className="cursor-pointer transition-colors hover:bg-slate-100"
                  >
                    <td className="whitespace-nowrap px-5 py-3 font-medium text-slate-900">
                      {user.first_name}
                    </td>
                    <td className="whitespace-nowrap px-5 py-3 text-slate-900">
                      {user.last_name}
                    </td>
                    <td className="whitespace-nowrap px-5 py-3 text-slate-700 text-xs">
                      {user.url}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* All-users expenses from date */}
      {users.length > 0 && (
        <AllUsersExpenses users={users} />
      )}

      {selectedUser && (
        <UserExpensesModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
        />
      )}
    </div>
  );
}

function formatExpenseDate(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
}

function AllUsersExpenses({ users }) {
  const [fromDate, setFromDate] = useState('');
  const [allExpenses, setAllExpenses] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('dated_on');
  const [sortDir, setSortDir] = useState('desc');
  const [filterUser, setFilterUser] = useState('');
  const [checkedSet, setCheckedSet] = useState(new Set());

  const userMap = useMemo(() => {
    const map = {};
    users.forEach((u) => {
      map[u.url] = `${u.first_name} ${u.last_name}`;
    });
    return map;
  }, [users]);

  const handleLoad = useCallback(async () => {
    if (!fromDate) return;
    setLoading(true);
    setError(null);
    try {
      const [expensesRes, paymentsRes] = await Promise.all([
        apiFetch('/getExpensesData'),
        apiFetch('/getExpensePayments'),
      ]);

      const expensesList = Array.isArray(expensesRes)
        ? expensesRes
        : Array.isArray(expensesRes?.expenses) ? expensesRes.expenses : [];

      setAllExpenses(expensesList.filter((e) => e.dated_on >= fromDate));

      const paymentsList = Array.isArray(paymentsRes) ? paymentsRes : [];
      setAllPayments(paymentsList.filter((p) => p.dated_on >= fromDate));

      setLoaded(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [fromDate]);

  const combined = useMemo(() => {
    const rows = [];

    allExpenses.forEach((e) => {
      rows.push({
        dated_on: e.dated_on,
        userName: userMap[e.user] || 'Unknown',
        userUrl: e.user,
        description: e.description || '',
        type: 'Expense',
        amount: Math.abs(parseFloat(e.gross_value) || 0),
      });
    });

    allPayments.forEach((p) => {
      rows.push({
        dated_on: p.dated_on,
        userName: userMap[p.paid_user] || p.user || 'Unknown',
        userUrl: p.paid_user,
        description: `Payment to ${userMap[p.paid_user] || p.user || 'Unknown'}`,
        type: 'Payment',
        amount: parseFloat(p.amount) || 0,
      });
    });

    return rows;
  }, [allExpenses, allPayments, userMap]);

  const userOptions = useMemo(() => {
    const names = [...new Set(combined.map((r) => r.userName))].sort();
    return names;
  }, [combined]);

  useEffect(() => {
    setCheckedSet(new Set());
  }, [filterUser, fromDate]);

  const filtered = useMemo(() => {
    if (!filterUser) return combined;
    return combined.filter((r) => r.userName === filterUser);
  }, [combined, filterUser]);

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir(col === 'dated_on' ? 'desc' : 'asc');
    }
  };

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      let va, vb;
      if (sortBy === 'amount') {
        va = a.amount;
        vb = b.amount;
      } else {
        va = (a[sortBy] || '').toLowerCase();
        vb = (b[sortBy] || '').toLowerCase();
      }
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [filtered, sortBy, sortDir]);

  const totalExpenses = sorted.filter((r) => r.type === 'Expense').reduce((s, r) => s + r.amount, 0);
  const totalPayments = sorted.filter((r) => r.type === 'Payment').reduce((s, r) => s + r.amount, 0);

  const allChecked = sorted.length > 0 && checkedSet.size === sorted.length;
  const someChecked = checkedSet.size > 0 && checkedSet.size < sorted.length;

  const toggleAll = useCallback(() => {
    if (allChecked) {
      setCheckedSet(new Set());
    } else {
      setCheckedSet(new Set(sorted.map((_, i) => i)));
    }
  }, [allChecked, sorted]);

  const toggleOne = useCallback((idx) => {
    setCheckedSet((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  }, []);

  const handleExportCsv = useCallback(() => {
    const rows = checkedSet.size > 0
      ? sorted.filter((_, i) => checkedSet.has(i))
      : sorted;
    const expTotal = rows.filter((r) => r.type === 'Expense').reduce((s, r) => s + r.amount, 0);
    const payTotal = rows.filter((r) => r.type === 'Payment').reduce((s, r) => s + r.amount, 0);
    const lines = ['Date,User,Description,Type,Amount'];
    rows.forEach((r) => {
      lines.push(
        `${formatExpenseDate(r.dated_on)},"${r.userName}","${r.description.replace(/"/g, '""')}",${r.type},${r.amount.toFixed(2)}`
      );
    });
    lines.push('');
    lines.push(`Total Expenses,,,,${expTotal.toFixed(2)}`);
    lines.push(`Total Payments,,,,${payTotal.toFixed(2)}`);
    const label = filterUser ? filterUser.replace(/\s+/g, '_') : 'all_users';
    downloadCsv(`expenses_from_${fromDate}_${label}.csv`, lines.join('\n'));
  }, [sorted, checkedSet, fromDate, filterUser]);

  function SortIcon({ active, direction }) {
    if (!active) return null;
    return (
      <svg
        className={`ml-1 inline h-3 w-3 text-slate-500 transition-transform ${direction === 'desc' ? 'rotate-180' : ''}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    );
  }

  return (
    <div className="rounded-xl bg-white shadow-md">
      <div className="px-5 py-4">
        <h3 className="mb-4 text-sm font-semibold text-slate-900">All User Expenses</h3>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="from-date" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              From Date
            </label>
            <input
              id="from-date"
              type="date"
              value={fromDate}
              onChange={(e) => { setFromDate(e.target.value); setLoaded(false); }}
              className="h-10 rounded-lg bg-slate-50 px-3 text-sm text-slate-900 shadow-sm outline-none focus:ring-2 focus:ring-slate-300"
            />
          </div>
          <button
            onClick={handleLoad}
            disabled={!fromDate || loading}
            className="h-10 rounded-lg bg-slate-900 px-5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Load Expenses'}
          </button>
          {loaded && combined.length > 0 && (
            <>
              <select
                value={filterUser}
                onChange={(e) => setFilterUser(e.target.value)}
                className="h-10 rounded-lg bg-slate-50 px-3 text-sm text-slate-900 shadow-sm outline-none focus:ring-2 focus:ring-slate-300"
              >
                <option value="">All Users</option>
                {userOptions.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
              <span className="text-xs text-slate-500">
                {sorted.length} row{sorted.length !== 1 ? 's' : ''}
              </span>
              <button
                onClick={handleExportCsv}
                className="inline-flex h-10 items-center gap-1.5 rounded-lg bg-emerald-600 px-4 text-sm font-medium text-white shadow-sm transition-colors hover:bg-emerald-700"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {checkedSet.size > 0 ? `Export ${checkedSet.size} Selected` : 'Export All'}
              </button>
            </>
          )}
        </div>
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      </div>

      {loaded && sorted.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="w-10 px-3 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    ref={(el) => { if (el) el.indeterminate = someChecked; }}
                    onChange={toggleAll}
                    className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                </th>
                <th className="cursor-pointer select-none px-5 py-3 transition-colors hover:bg-slate-100" onClick={() => handleSort('dated_on')}>
                  Date <SortIcon active={sortBy === 'dated_on'} direction={sortDir} />
                </th>
                <th className="cursor-pointer select-none px-5 py-3 transition-colors hover:bg-slate-100" onClick={() => handleSort('userName')}>
                  User <SortIcon active={sortBy === 'userName'} direction={sortDir} />
                </th>
                <th className="cursor-pointer select-none px-5 py-3 transition-colors hover:bg-slate-100" onClick={() => handleSort('description')}>
                  Description <SortIcon active={sortBy === 'description'} direction={sortDir} />
                </th>
                <th className="cursor-pointer select-none px-5 py-3 transition-colors hover:bg-slate-100" onClick={() => handleSort('type')}>
                  Type <SortIcon active={sortBy === 'type'} direction={sortDir} />
                </th>
                <th className="cursor-pointer select-none px-5 py-3 text-right transition-colors hover:bg-slate-100" onClick={() => handleSort('amount')}>
                  Amount <SortIcon active={sortBy === 'amount'} direction={sortDir} />
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sorted.map((row, i) => (
                <tr key={i} className={`transition-colors ${checkedSet.has(i) ? 'bg-indigo-50' : 'hover:bg-slate-50'}`}>
                  <td className="w-10 px-3 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={checkedSet.has(i)}
                      onChange={() => toggleOne(i)}
                      className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                  </td>
                  <td className="whitespace-nowrap px-5 py-3 text-slate-900">{formatExpenseDate(row.dated_on)}</td>
                  <td className="whitespace-nowrap px-5 py-3 font-medium text-slate-900">{row.userName}</td>
                  <td className="px-5 py-3 text-slate-700">{row.description}</td>
                  <td className="whitespace-nowrap px-5 py-3">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                      row.type === 'Expense' ? 'bg-blue-100 text-blue-800' : 'bg-emerald-100 text-emerald-800'
                    }`}>
                      {row.type}
                    </span>
                  </td>
                  <td className={`whitespace-nowrap px-5 py-3 text-right font-medium ${
                    row.type === 'Expense' ? 'text-blue-700' : 'text-emerald-700'
                  }`}>
                    {toCurrency(row.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-slate-50 text-sm font-semibold">
              <tr>
                <td className="px-5 py-3 text-slate-900" colSpan={4}>Total</td>
                <td className="px-5 py-3 text-blue-700">Expenses</td>
                <td className="px-5 py-3 text-right text-blue-700">{toCurrency(totalExpenses)}</td>
              </tr>
              <tr className="border-t border-slate-200">
                <td className="px-5 py-3" colSpan={4} />
                <td className="px-5 py-3 text-emerald-700">Payments</td>
                <td className="px-5 py-3 text-right text-emerald-700">{toCurrency(totalPayments)}</td>
              </tr>
              <tr className="border-t border-slate-200">
                <td className="px-5 py-3" colSpan={4} />
                <td className="px-5 py-3 text-slate-900">Delta</td>
                <td className={`px-5 py-3 text-right ${totalExpenses - totalPayments > 0 ? 'text-red-600' : 'text-slate-900'}`}>
                  {totalExpenses - totalPayments > 0 ? '−' : ''}{toCurrency(Math.abs(totalExpenses - totalPayments))}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {loaded && sorted.length === 0 && (
        <div className="px-5 pb-5 text-center text-sm text-slate-500">
          No expenses or payments found from {formatExpenseDate(fromDate)}.
        </div>
      )}
    </div>
  );
}
